import { useMemo } from 'react';
import { useTrip } from '../lib/tripContext.jsx';
import { useTripJson, useWeather } from '../hooks/data.js';

// Mendenhall Lake canoe expedition — Thursday 2026-05-28, 14:30–19:30.
// Pulls the canonical record from excursions.json (id: thu_mendenhall_canoe)
// and adds a focused weather panel for the paddle window. The lake is
// closed to motorized boats, so wind + precip are the only meaningful
// variables for this trip.

const EXCURSION_ID = 'thu_mendenhall_canoe';
const LAKE_LAT = 58.4147;
const LAKE_LNG = -134.5520;
const LAKE_TZ = 'America/Juneau';

// Wind threshold from the excursion record's weather_note: "sustained
// wind > 15 kt or thunderstorms cancel". 15 kt ≈ 27.78 km/h.
const WIND_CANCEL_KMH = 27.78;
const WIND_CAUTION_KMH = 16; // ~9 kt — choppy enough to slow the paddle

function dayKeyInTz(date, tz) {
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return fmt.format(date);
}

function classifyConditions(rows) {
  if (!rows || rows.length === 0) return { label: 'pending', tone: 'neutral' };
  const maxWind = Math.max(...rows.map((r) => r.wind || 0));
  const maxGust = Math.max(...rows.map((r) => r.gust || 0));
  const totalPrecip = rows.reduce((s, r) => s + (r.precip || 0), 0);
  const heavyRainHours = rows.filter((r) => (r.precip || 0) >= 2).length;
  if (maxGust >= WIND_CANCEL_KMH || maxWind >= WIND_CANCEL_KMH) {
    return { label: 'likely cancel', tone: 'danger', reason: `gusts ${Math.round(maxGust)} km/h ≥ 28` };
  }
  if (maxWind >= WIND_CAUTION_KMH || heavyRainHours >= 2) {
    return { label: 'wet + workout', tone: 'caution', reason: `${Math.round(maxWind)} km/h sustained · ${totalPrecip.toFixed(1)} mm precip` };
  }
  return { label: 'manageable', tone: 'good', reason: `${Math.round(maxWind)} km/h sustained · ${totalPrecip.toFixed(1)} mm precip` };
}

const TONE_CLASSES = {
  good: 'border-success/40 bg-success/10 text-success',
  caution: 'border-copper/40 bg-copper/10 text-copper-dark',
  danger: 'border-danger/40 bg-danger/10 text-danger',
  neutral: 'border-border-tertiary bg-bg-secondary text-fg-tertiary',
};

export default function MendenhallCanoe() {
  const { tripId } = useTrip();
  const data = useTripJson(tripId, 'excursions.json');
  const weather = useWeather({ lat: LAKE_LAT, lng: LAKE_LNG, hours: 168, timezone: LAKE_TZ });

  const excursion = useMemo(
    () => (data.data?.excursions || []).find((e) => e.id === EXCURSION_ID) || null,
    [data.data]
  );

  // Slice hourly weather to the actual paddle window: Thu 2026-05-28
  // 14:00 → 20:00 local. Open-Meteo returns hourly arrays in the
  // requested timezone so we can compare ISO timestamps directly.
  const paddleHours = useMemo(() => {
    if (!weather.data || !excursion?.booking) return [];
    const b = excursion.booking;
    const startISO = `${b.date_iso}T${b.start_local}:00`;
    const endISO = `${b.date_iso}T${b.end_local}:00`;
    const times = weather.data.hourly?.time || [];
    const out = [];
    for (let i = 0; i < times.length; i += 1) {
      const t = times[i];
      if (t >= startISO && t <= endISO) {
        out.push({
          time: t,
          temp: weather.data.hourly?.temperature_2m?.[i],
          wind: weather.data.hourly?.wind_speed_10m?.[i],
          gust: weather.data.hourly?.wind_gusts_10m?.[i],
          precip: weather.data.hourly?.precipitation?.[i],
          cloud: weather.data.hourly?.cloud_cover?.[i],
        });
      }
    }
    return out;
  }, [weather.data, excursion]);

  const verdict = useMemo(() => classifyConditions(paddleHours), [paddleHours]);
  const tripDate = excursion?.booking?.date_iso;
  const isToday = tripDate && tripDate === dayKeyInTz(new Date(), LAKE_TZ);

  if (data.loading) {
    return (
      <div className="mx-auto w-full max-w-screen-sm p-4">
        <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
          …loading excursion
        </p>
      </div>
    );
  }
  if (!excursion) {
    return (
      <div className="mx-auto w-full max-w-screen-sm p-4">
        <p className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 font-mono text-[11px] text-danger">
          Mendenhall canoe record missing from excursions.json.
        </p>
      </div>
    );
  }

  const dateLabel = tripDate
    ? new Date(`${tripDate}T12:00:00Z`).toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'short',
        day: 'numeric',
        timeZone: LAKE_TZ,
      })
    : '';

  return (
    <div className="mx-auto w-full max-w-screen-sm space-y-4 p-4">
      <header>
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-fg-tertiary">
          excursion · active
        </p>
        <h1 className="mt-1 font-serif text-2xl">{excursion.name}</h1>
        <p className="mt-1 text-sm text-fg-secondary">{excursion.summary}</p>
      </header>

      <section className="card overflow-hidden border-success/40">
        <header className="space-y-2 border-b border-success/30 bg-success/5 px-4 py-3">
          <p className="font-mono text-[11px] uppercase tracking-wider text-fg-secondary">
            {dateLabel} · {excursion.booking?.start_local}–{excursion.booking?.end_local} ·{' '}
            {excursion.duration_hours}h
          </p>
          <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
            ${excursion.booking?.per_person}/person · group of 3 = $
            {excursion.booking?.group_total?.toLocaleString?.() ||
              excursion.group_total_for_3.toLocaleString()}{' '}
            · via {excursion.booking?.aggregator || '—'}
          </p>
        </header>

        <div className="space-y-3 px-4 py-3">
          {excursion.format_note && (
            <p className="rounded-md border border-copper/40 bg-copper/10 p-2 text-xs text-copper-dark">
              <span className="font-mono text-[10px] uppercase tracking-wider">format · </span>
              {excursion.format_note}
            </p>
          )}
          {excursion.what_to_expect && (
            <p className="text-sm text-fg-primary">{excursion.what_to_expect}</p>
          )}
          {excursion.booking?.context_note && (
            <p className="rounded-md border border-copper/30 bg-copper/5 p-2 text-xs text-fg-secondary">
              <span className="font-mono text-[10px] uppercase tracking-wider text-copper-dark">
                heads-up ·{' '}
              </span>
              {excursion.booking.context_note}
            </p>
          )}
        </div>
      </section>

      <section
        aria-labelledby="lake-forecast"
        className="card overflow-hidden"
      >
        <header className="border-b border-border-tertiary bg-bg-secondary px-4 py-3">
          <h2
            id="lake-forecast"
            className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary"
          >
            mendenhall lake · paddle window forecast
          </h2>
          <p className="mt-1 font-serif text-base">
            {weather.loading
              ? '…loading lake forecast'
              : paddleHours.length === 0
                ? 'forecast loads closer to trip dates'
                : (
                    <>
                      <span
                        className={[
                          'rounded-full border px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider',
                          TONE_CLASSES[verdict.tone],
                        ].join(' ')}
                      >
                        {verdict.label}
                      </span>
                      {verdict.reason && (
                        <span className="ml-2 font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
                          {verdict.reason}
                        </span>
                      )}
                    </>
                  )}
          </p>
        </header>
        {paddleHours.length > 0 && (
          <ul className="divide-y divide-border-tertiary">
            {paddleHours.map((h) => (
              <li
                key={h.time}
                className="flex items-baseline justify-between gap-3 px-4 py-2 font-mono text-[11px] text-fg-secondary"
              >
                <span className="uppercase tracking-wider text-fg-tertiary">
                  {h.time.slice(11, 16)}
                </span>
                <span className="flex-1 normal-case tracking-normal">
                  {Math.round(h.temp ?? 0)}°C · cloud {Math.round(h.cloud ?? 0)}%
                </span>
                <span className="normal-case tracking-normal">
                  wind {Math.round(h.wind ?? 0)}{' '}
                  {h.gust ? `· gust ${Math.round(h.gust)}` : ''} km/h
                </span>
                <span className="w-12 text-right normal-case tracking-normal">
                  {(h.precip || 0).toFixed(1)} mm
                </span>
              </li>
            ))}
          </ul>
        )}
        {weather.error && (
          <p className="border-t border-border-tertiary px-4 py-2 font-mono text-[10px] text-danger">
            weather unavailable — {weather.error.message}
          </p>
        )}
        <p className="border-t border-border-tertiary bg-bg-secondary px-4 py-2 font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
          cancel threshold · sustained wind ≥ 15 kt (≈28 km/h) or thunderstorms
        </p>
      </section>

      {(excursion.highlights?.length || 0) > 0 && (
        <section
          aria-labelledby="highlights"
          className="card overflow-hidden"
        >
          <header className="border-b border-border-tertiary bg-bg-secondary px-4 py-2">
            <h2
              id="highlights"
              className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary"
            >
              what you&apos;ll see
            </h2>
          </header>
          <ul className="space-y-1 px-4 py-3 text-sm text-fg-secondary">
            {excursion.highlights.map((h) => (
              <li key={h} className="flex gap-2">
                <span className="text-fg-tertiary" aria-hidden>
                  ◦
                </span>
                <span>{h}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section
        aria-labelledby="prep"
        className="card overflow-hidden"
      >
        <header className="border-b border-border-tertiary bg-bg-secondary px-4 py-2">
          <h2
            id="prep"
            className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary"
          >
            what to bring + prep
          </h2>
        </header>
        <div className="space-y-3 px-4 py-3 text-sm text-fg-secondary">
          {excursion.what_to_bring?.length > 0 && (
            <ul className="space-y-1">
              {excursion.what_to_bring.map((p) => (
                <li key={`b-${p}`} className="flex gap-2">
                  <span className="text-fg-tertiary" aria-hidden>
                    □
                  </span>
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          )}
          {excursion.prep_checklist?.length > 0 && (
            <ul className="space-y-1 border-t border-border-tertiary pt-2">
              {excursion.prep_checklist.map((p) => (
                <li key={`p-${p}`} className="flex gap-2">
                  <span className="text-fg-tertiary" aria-hidden>
                    →
                  </span>
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {isToday && (
        <p className="rounded-md border border-action/40 bg-action/10 px-3 py-2 font-mono text-[11px] uppercase tracking-wider text-action">
          today — eat lunch by 1pm · queue your offline submissions before launch
        </p>
      )}
    </div>
  );
}
